<template>
  <div class="page-container">
    <!-- 主体容器 -->
    <div class="main-container">
      <!-- 上部分：电价名称相关 -->
      <div class="top-section-block">
        <!-- 头部容器 -->
        <div class="header-container">
          <!-- 标题 -->
          <div class="title-container">
            <div class="mode-title-row" :class="{ disabled: !currentModeElecPriceId }" @click="handleModeTitleClick">
              <span class="mode-title-label">当前模式：</span>
              <span class="mode-title-value">{{ currentModeLabel }}</span>
              <el-icon class="mode-edit-icon">
                <Edit />
              </el-icon>
              <!-- <span class="mode-click-tip">点击切换</span> -->
            </div>
            <div class="manage-row">
              <!-- <span class="title-text">{{ $str.getKey('收益管理/电价管理') }}</span> -->
              <span class="title-span">{{ $str.getKey('收益管理/开启后按最新国网电价定期生成，关闭则需手动录入电价。') }}</span>
              <el-switch v-model="changePrice" class="ml-2" :disabled="!canAddElectricPrice" @change="changeCheck" />
            </div>
          </div>
          <!-- 操作按钮 -->
          <div class="btns-container">
            <el-button type="primary" class="add-price-btn" :icon="Plus" :disabled="!canAddElectricPrice"
              @click="onAddPrice" v-track-click="trackDatas.addPriceBtn">{{ $str.getKey('收益管理/新增电价') }}</el-button>
          </div>
        </div>
        <!-- 电价面板 -->
        <div class="card-list-container">
          <div class="cards-container" v-for="(item, idx) in cardList" :key="idx" v-if="cardList.length > 0">
            <ElectrovalenceCard class="card-item" :priceInfo="item" v-if="!needFreshCard"
              @onEdit="editCustomPrice(item)" />
          </div>
          <el-empty class="empty-card" v-else-if="!loading && !canViewElectricPrice" :image="noDataIcon"
            :description="$str.getKey('收益管理/没有权限')"></el-empty>
          <el-empty class="empty-card" v-else-if="!loading" :image="noDataIcon"
            :description="$str.getKey('收益管理/暂无数据')"></el-empty>
        </div>
      </div>
      <!-- 下部分：电价历史 -->
      <div class="bottom-section-block">
        <ElecPriceHistory ref="historyPanel" @refreshData="handleRefresh()" @reCreate="handleReCreate" />
      </div>
    </div>



    <!-- ====== 弹窗：新增&编辑-自定义电价 ======================================================================================================================== -->
    <AddAndEditModal v-if="modalOption.isShow" v-model:show="modalOption.isShow" :openType="modalOption.openType"
      :elecPriceId="modalOption.elecPriceId" @onClose="handleModalClose" />
  </div>
</template>

<script setup>
import { ref, reactive, computed, nextTick, inject, watch } from "vue";
import { Plus, Edit } from "@element-plus/icons-vue";
import dayjs from 'dayjs';
import { queryElecPriceInUse, querySwitch, updateSwitch } from '../components/elec-trovalency/api/index.js';
import { checkPermi } from '@/utils/permission';
import AddAndEditModal from "../components/elec-trovalency/addAndEditModal.vue";
import ElectrovalenceCard from "../components/elec-trovalency/electrovalenceCard.vue"
import ElecPriceHistory from "../components/elec-trovalency/elecPriceHistory.vue";
import noDataIcon from "@/assets/noPic.svg";
import useAppStore from "@/store/modules/app";
import { getElecPriceSegmentColor } from '@/utils/colorHelper/index.js';
import { getElecPriceSegmentName } from '@/utils/nameHelper/index.js';
import { ElMessageBox } from "element-plus";
import { $t, transformI18n } from "@/plugins/i18n";

const $str = window.$str;

const trackEvent = inject('trackEvent', () => { });
const trackEventTypes = inject('trackEventTypes', {
  edit: 'edit',
  otherOperate: 'otherOperate'
});
const trackDatas = reactive({
  addPriceBtn: {
    targetId: '',
    refData: {
      targetName: $str.getKey('收益管理/按钮：新增电价'),
      desc: $str.getKey('收益管理/点击按钮，进入新增电价弹窗界面')
    },
  },
  editPriceBtn: {
    targetId: '',
    refData: {
      targetName: $str.getKey('收益管理/按钮：编辑'),
      desc: $str.getKey('收益管理/点击按钮，进入编辑电价弹窗界面')
    },
  },
  createTypeSwitch: {
    targetId: '',
    refData: {
      targetName: $str.getKey('收益管理/开关：是否按最新国网电价定期生成'),
      desc: $str.getKey('收益管理/开启后按最新国网电价定期生成，关闭则需手动录入电价。')
    },
  },
})


const loading = ref(false); // 控制加载中动效的渲染
const historyPanel = ref(null); // 引用组件：ElecPriceHistory
const appStore = useAppStore();
/** 获取stationId，并监听stationId的变化，触发刷新 */
const stationId = computed(() => appStore.stationId);

const changePrice = ref(true); // 是否开启：按最新国网电价定期生成电价的功能。
const cardList = ref([]); // 电价列表的图表数据
const needFreshCard = ref(false); // 控制card列表渲染的刷新
const currentModeType = ref(null); // 当前模式类型：1代理购电 3自定义
const currentModeElecPriceId = ref('');
const currentModeLabel = computed(() => {
  if (currentModeType.value === 1) return '代理购电电价';
  if (currentModeType.value === 3) return '自定义电价';
  return '--';
})
const canViewElectricPrice = computed(() => checkPermi(['operate:electricPrice:query']));
const canAddElectricPrice = computed(() => checkPermi(['operate:electricPrice:add']));
const canEditElectricPrice = computed(() => checkPermi(['operate:electricPrice:edit']));

// 监听 stationId 变化
watch(() => appStore.stationId, (newStationId) => {
  if (newStationId) {
    refreshCardListData();
  }
}, { immediate: true })



const modalOption = reactive({ // 用于控制 AddAndEditPriceModal 的配置对象
  isShow: false,
  openType: '', // 'add' | 'edit'
  elecPriceId: '', // 电价id
})


/** ====== [业务：功能方法] =================================================================== */
/** 新增电价 */
function onAddPrice() {
  if (!canAddElectricPrice.value) return;
  modalOption.openType = 'add';
  modalOption.isShow = true;
  modalOption.elecPriceId = '';
}

/** card：电价数据-编辑 >>> 目前只有inUse的电价才能被编辑 */
function editCustomPrice(data) {
  if (!canEditElectricPrice.value) return;
  modalOption.elecPriceId = data.elecPriceId;
  modalOption.openType = 'edit';
  modalOption.isShow = true;

  // 埋点信息：
  trackDatas.editPriceBtn.refData['stationId'] = stationId.value;
  trackDatas.editPriceBtn.refData['elecPriceId'] = data.elecPriceId;
  trackEvent(trackEventTypes.edit, trackDatas.editPriceBtn);
}

/** 头部：当前模式点击，行为与“编辑”一致 */
function handleModeTitleClick() {
  if (!currentModeElecPriceId.value) return;
  if (!canEditElectricPrice.value) return;
  editCustomPrice({ elecPriceId: currentModeElecPriceId.value });
}


/** 复用-基于id对应的电价，重新创建新电价 */
function handleReCreate(data) {
  if (!checkPermi(['operate:electricPrice:reuse'])) return;
  if (data?.elecPriceId) {
    if (!data.isCoverSelf) {
      // 将id传递给弹窗组件，在弹窗组件中获取电价信息，并回显
      modalOption.elecPriceId = data.elecPriceId;
      modalOption.openType = 'reCreate';
      modalOption.isShow = true;
    }
  } else {
    console.error('调用 handleReCreate 方法时，必须传入电价ID！');
  }
}



/** 处理弹窗的关闭事件 */
function handleModalClose(needRefresh = false) {
  modalOption.isShow = false;
  modalOption.openType = '';
  modalOption.elecPriceId = '';
  // 关闭弹窗后，是否需要刷新页面数据
  if (needRefresh) {
    handleRefresh();
  }
}

/** 刷新页面数据 */
function handleRefresh() {
  getCheck();   // 获取国网电价配置开关状态
  refreshCardListData(); // 刷新【正在使用电价】卡片列表的数据
  historyPanel.value.refreshHistoryList(); // 刷新【电价历史】列表的数据

}



/** ====== [辅助：功能方法] =================================================================== */

/** 获取电价列表的图表数据 */
function refreshCardListData() {
  if (!canViewElectricPrice.value) {
    console.log('当前用户没有权限查看正在使用的电价信息')
    currentModeType.value = null;
    currentModeElecPriceId.value = '';
    return false; // 当前用户没有权限查看正在使用的电价信息
  }
  loading.value = true;
  getCheck();
  queryElecPriceInUse(stationId.value).then(res => {
    if (Array.isArray(res?.data)) {
      const resList = [];
      const currentItem = res.data.find((it) => it && it.type !== undefined && it.type !== null) || null;
      currentModeType.value = currentItem ? Number(currentItem.type) : null;
      currentModeElecPriceId.value = currentItem?.id || '';
      res.data.forEach(item => {
        const cardItem = {
          elecPriceId: item.id,
          stationId: item.stationId,
          elecPriceName: item.priceName,
          // elecPriceName: getElecPriceName(item),
          type: item.type, // 0基础电价 1自定义时段电价
          chargeType: item.chargeType, // 0-充电电价 1-放电电价 2-充放电价相同
          startDate: item.startDate,
          endDate: item.endDate,
          priceDetails: [],
          isShowChart: false,
        }
        if (Array.isArray(item.priceDetails)) {
          cardItem.priceDetails = item.priceDetails.map(it => {
            return {
              name: getElecPriceSegmentName(it.name),
              price: it.price ?? '-',
              startTime: it.startTime,
              endTime: it.endTime,
              // code: it.code, // ??? 后端在用，前端忽略
              color: getElecPriceSegmentColor(it.name),
            }
          })
          cardItem.isShowChart = cardItem.priceDetails.every(it => {
            return checkTimeFormat(it.startTime) && checkTimeFormat(it.endTime);
          })
        }

        resList.push(cardItem);
      });
      cardList.value = resList;
      // 刷新图表
      needFreshCard.value = true;
      nextTick(() => {
        needFreshCard.value = false;
      })
    }
    else {
      currentModeType.value = null;
      currentModeElecPriceId.value = '';
    }

  }).catch(err => {
    cardList.value = [];
    currentModeType.value = null;
    currentModeElecPriceId.value = '';
    console.error('获取正在使用电价失败', err);
  }).finally(() => {
    loading.value = false;
  })
}


/** 获取电价名称 */
function getElecPriceName(data) {
  // 0-充电电价 1-放电电价 2-充放电价相同
  const chargeType = data.chargeType;
  const originName = data.priceName;
  if (chargeType === 0) {
    return originName + $str.getKey('收益管理/充电电价');
  } else if (chargeType === 1) {
    return originName + $str.getKey('收益管理/放电电价');
  } else {
    return originName;
  }
}


/** 检查时间格式是否为 HH:mm  */
function checkTimeFormat(timeString) {
  // 正则表达式：匹配 'HH:mm' 格式
  const regex = /^(0[0-9]|1[0-2]):([0-5][0-9])$/;
  return regex.test(timeString);
}


/** 切换自动生成电价的方式 */
function getCheck() {
  querySwitch(stationId.value).then(res => {
    changePrice.value = Boolean(res && res.data && res.data.priceSwitch)
  })
}

function changeCheck() {
  let lodSwitch = changePrice.value
  // 确认是否切换电价
  ElMessageBox.confirm(
    $str.getKey('收益管理/是否改变电价记录生成方式?'),
    $str.getKey('收益管理/提示'),
    {
      confirmButtonText: $str.getKey('收益管理/确定'),
      cancelButtonText: $str.getKey('收益管理/取消'),
      type: "warning"
    }
  ).then(() => {
    updateSwitch(stationId.value, changePrice.value)
    // 埋点信息：
    trackDatas.createTypeSwitch.refData['stationId'] = stationId.value;
    trackDatas.createTypeSwitch.refData['switchValue'] = changePrice.value;
    trackEvent(trackEventTypes.otherOperate, trackDatas.createTypeSwitch);
  }).catch(() => {
    changePrice.value = lodSwitch
  });
}

</script>
<style lang="scss" scoped>
.page-container {
  --page-top: 12px;
  --header-hight: 50px;

  & {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    height: calc(100% - var(--page-top));
    background: var(--level1-bg-color);
    margin-top: var(--page-top);
    padding: 10px;

    border-radius: 6px;
    box-sizing: border-box;
    overflow-x: hidden;

    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .main-container {
    --card-height: 360px;

    & {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      height: 100%;
      flex: 1;
      min-height: 0;
      overflow: auto;
      background: transparent;
      padding: 0;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      gap: 12px;
    }

    .top-section-block {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      background: #ffffff;
      border-radius: 20px;
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
      border: 1px solid rgba(15, 23, 42, 0.02);
      padding: 20px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      flex-shrink: 0;
      overflow: hidden;
    }

    .bottom-section-block {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      background: #ffffff;
      border-radius: 20px;
      box-shadow: 0 8px 24px rgba(15, 23, 42, 0.04);
      border: 1px solid rgba(15, 23, 42, 0.02);
      padding: 20px;
      box-sizing: border-box;
      flex: 0 0 auto;
      display: flex;
      flex-direction: column;
      min-height: 500px;
      align-items: stretch;
      overflow: hidden;
    }

    .header-container {
      width: 100%;
      height: auto;
      min-height: var(--header-hight);
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      margin-bottom: 20px;
      flex-shrink: 0;
      min-width: 0;
      flex-wrap: wrap;

      .title-container {
        flex: 1 1 360px;
        max-width: 100%;
        min-width: 0;
        min-height: 100%;
        padding-left: 0;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        gap: 6px;

        .manage-row {
          width: 100%;
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          min-width: 0;
        }

        .title-text {
          color: #2E2E2E;
          font-family: "HarmonyOS Sans SC";
          font-size: 16px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
          flex-shrink: 0;
        }

        .title-span {
          color: #96A5B8;
          font-family: "HarmonyOS Sans SC";
          font-size: 13px;
          font-style: normal;
          font-weight: 400;
          line-height: 16px;
          /* 123.077% */
          min-width: 0;
          white-space: normal;
          overflow-wrap: anywhere;
          text-overflow: ellipsis;
        }

        .mode-span {
          color: #2E2E2E;
          font-family: "HarmonyOS Sans SC";
          font-size: 13px;
          font-style: normal;
          font-weight: 400;
          line-height: 16px;
          flex-shrink: 0;
        }

        .mode-title-row {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          user-select: none;
          border-radius: 8px;
          transition: background-color 0.2s ease;

          &:hover {
            background: rgba(24, 98, 245, 0.06);
          }

          &.disabled {
            cursor: default;
            opacity: 0.55;

            &:hover {
              background: transparent;
            }
          }
        }

        .mode-title-label {
          color: #2E2E2E;
          font-family: "HarmonyOS Sans SC";
          font-size: 16px;
          font-style: normal;
          font-weight: 500;
          line-height: normal;
        }

        .mode-title-value {
          color: #2E2E2E;
          font-family: "HarmonyOS Sans SC";
          font-size: 16px;
          font-style: normal;
          font-weight: 500;
          line-height: normal;
        }

        .mode-edit-icon {
          color: #1862F5;
          font-size: 14px;
        }

        .mode-click-tip {
          color: #96A5B8;
          font-family: "HarmonyOS Sans SC";
          font-size: 12px;
          font-style: normal;
          font-weight: 400;
          line-height: normal;
        }

        // 开关按钮样式，参考新增电价页面，使用绿色
        :deep(.el-switch) {
          --el-switch-on-color: #00DD7A;
        }
      }

      .btns-container {
        flex: 0 0 auto;
        max-width: 100%;
        height: auto;
        padding-right: 0;
        display: flex;
        justify-content: flex-end;
        align-items: center;
        // gap: 10px;

        // 新增电价按钮样式(系统统一主色)
        :deep(.add-price-btn) {
          min-width: 99px !important;
          height: 40px !important;
          min-height: 40px !important;
          max-height: 40px !important;
          border-radius: 12px !important;
          border: 1px solid var(--color-primary) !important;
          padding: 0 12px !important;
          box-sizing: border-box !important;
          color: #ffffff !important;
          font-family: "HarmonyOS Sans SC" !important;
          font-size: 14px !important;
          font-style: normal !important;
          font-weight: 400 !important;
          line-height: normal !important;
          background-color: var(--color-primary) !important;

          &:hover {
            background-color: var(--color-primary) !important;
            border-color: var(--color-primary) !important;
            color: #ffffff !important;
            opacity: 0.9;
          }

          &:active {
            background-color: var(--color-primary) !important;
            border-color: var(--color-primary) !important;
            color: #ffffff !important;
            opacity: 0.85;
          }

          &.is-disabled,
          &.is-disabled:hover {
            opacity: 0.5;
          }

          // 确保图标颜色
          .el-icon {
            color: #ffffff !important;
          }

          span {
            color: #ffffff !important;
          }
        }
      }
    }

    .card-list-container {
      width: 100%;
      max-width: 100%;
      min-width: 0;
      min-height: var(--card-height);
      border-radius: 0;
      background: transparent;
      overflow: hidden;

      .cards-container {
        width: 100%;
        max-width: 100%;
        min-width: 0;
        display: flex;
        justify-content: center;
      }

      .empty-card {
        min-height: var(--card-height);
      }
    }

    .history-list-container {
      width: 100%;
      flex: 1;
      min-height: 500px;
      border-radius: 0;
      background: transparent;
      padding: 0;
      margin: 0;
    }
  }

}
</style>
